
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Mail, Phone, Globe, Map, Navigation } from 'lucide-react';

interface OrderFormDetailsSectionProps {
  shippingAddress: string;
  notes: string;
  handleShippingAddressChange: (value: string) => void;
  handleNotesChange: (value: string) => void;
  customerCountry?: string;
  customerRegion?: string;
  customerMunicipality?: string;
  customerEmail?: string;
  customerPhone?: string;
  setCustomerCountry?: (value: string) => void;
  setCustomerRegion?: (value: string) => void;
  setCustomerMunicipality?: (value: string) => void;
}

const OrderFormDetailsSection = ({
  shippingAddress,
  notes,
  handleShippingAddressChange,
  handleNotesChange,
  customerCountry = '',
  customerRegion = '',
  customerMunicipality = '',
  customerEmail = '',
  customerPhone = '',
  setCustomerCountry,
  setCustomerRegion,
  setCustomerMunicipality
}: OrderFormDetailsSectionProps) => {
  
  // Log current values for debugging
  React.useEffect(() => {
    console.log("OrderFormDetailsSection rendering with values:", {
      customerCountry,
      customerRegion,
      customerMunicipality,
      shippingAddress
    });
  }, [customerCountry, customerRegion, customerMunicipality, shippingAddress]);
  
  return (
    <Card className="md:col-span-2 border-0 shadow-none bg-white">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl font-semibold text-gray-800">Détails de la commande</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="shipping" className="w-full">
          <TabsList className="w-full border-b mb-6 bg-transparent p-0 h-auto space-x-6">
            <TabsTrigger 
              value="shipping" 
              className="py-3 px-0 bg-transparent rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-indigo-600 data-[state=active]:border-b-2 data-[state=active]:border-indigo-600"
            >
              Livraison
            </TabsTrigger>
            <TabsTrigger 
              value="notes" 
              className="py-3 px-0 bg-transparent rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-indigo-600 data-[state=active]:border-b-2 data-[state=active]:border-indigo-600"
            >
              Notes
            </TabsTrigger>
            {customerEmail && (
              <TabsTrigger 
                value="contact" 
                className="py-3 px-0 bg-transparent rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-indigo-600 data-[state=active]:border-b-2 data-[state=active]:border-indigo-600"
              >
                Contact
              </TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="shipping" className="space-y-4 mt-0">
            <div className="grid grid-cols-1 gap-4 mb-4">
              <div className="flex gap-2 items-center mb-2">
                <Globe className="h-4 w-4 text-gray-500" />
                <Label className="text-sm font-medium text-gray-700">Informations de localisation</Label>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="municipality" className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                  <Navigation className="h-4 w-4 text-gray-500" />
                  Ville / Municipalité
                </Label>
                <Input
                  id="municipality"
                  value={customerMunicipality}
                  onChange={(e) => setCustomerMunicipality?.(e.target.value)}
                  placeholder="Entrez la ville ou municipalité"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="region" className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                  <Map className="h-4 w-4 text-gray-500" />
                  Région / Wilaya
                </Label>
                <Input
                  id="region"
                  value={customerRegion}
                  onChange={(e) => setCustomerRegion?.(e.target.value)}
                  placeholder="Entrez la région ou wilaya"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="country" className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                  <Globe className="h-4 w-4 text-gray-500" />
                  Pays
                </Label>
                <Input
                  id="country"
                  value={customerCountry}
                  onChange={(e) => setCustomerCountry?.(e.target.value)}
                  placeholder="Entrez le pays"
                  className="w-full"
                  defaultValue="Algeria"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="shippingAddress" className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                <MapPin className="h-4 w-4 text-gray-500" />
                Adresse détaillée
              </Label>
              <Textarea 
                id="shippingAddress" 
                value={shippingAddress} 
                onChange={(e) => handleShippingAddressChange(e.target.value)} 
                placeholder="Entrez l'adresse de livraison complète"
                rows={4}
                className="min-h-[120px] resize-y"
              />
            </div>
          </TabsContent>
          
          <TabsContent value="notes" className="space-y-4 mt-0">
            <div>
              <Label htmlFor="notes" className="mb-2 block text-sm font-medium text-gray-700">Notes</Label>
              <Textarea 
                id="notes" 
                value={notes} 
                onChange={(e) => handleNotesChange(e.target.value)} 
                placeholder="Ajouter des notes pour cette commande"
                rows={6}
                className="min-h-[180px] resize-y"
              />
            </div>
          </TabsContent>
          
          {customerEmail && (
            <TabsContent value="contact" className="space-y-4 mt-0">
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-md text-sm flex items-start">
                  <Mail className="h-4 w-4 text-gray-500 mr-2 mt-0.5" />
                  <span>{customerEmail}</span>
                </div>
                
                {customerPhone && (
                  <div className="p-3 bg-slate-50 rounded-md text-sm flex items-start">
                    <Phone className="h-4 w-4 text-gray-500 mr-2 mt-0.5" />
                    <span>{customerPhone}</span>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default OrderFormDetailsSection;
